<script setup lang="ts" generic="T extends string">
export interface ModeSegmentOption<TValue extends string> {
  value: TValue;
  label: string;
  icon: 'track-target' | 'trending-up';
}

const props = defineProps<{
  modelValue: T;
  options: ModeSegmentOption<T>[];
}>();

const emit = defineEmits<{
  'update:modelValue': [value: T];
}>();

function select(value: T): void {
  emit('update:modelValue', value);
}

function isActive(option: ModeSegmentOption<T>): boolean {
  return option.value === props.modelValue;
}
</script>

<template>
  <div class="relative w-full h-9 rounded-pill bg-grey-50 p-0.5 select-none">
    <div class="flex relative w-full h-full">
      <div
        v-for="option in options"
        :key="option.value"
        class="flex-1"
      />
    </div>
    <div
      class="absolute top-0.5 bottom-0.5 rounded-pill bg-bg-card shadow-mode-pill transition-all duration-spring ease-spring"
      :style="{
        width: `calc(100% / ${options.length})`,
        left: `calc(${options.findIndex(isActive)} * (100% / ${options.length}) + 2px)`,
      }"
    />
    <div class="absolute inset-0 flex">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        class="flex-1 flex items-center justify-center gap-1.5 text-label-medium font-medium rounded-pill outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-1 focus-visible:ring-offset-grey-50 transition-colors duration-fast hover:bg-grey-200 active:bg-grey-300"
        :class="isActive(option) ? 'text-text-primary font-bold' : 'text-text-secondary'"
        @click="select(option.value)"
      >
        <svg v-if="option.icon === 'track-target'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="4" />
          <line x1="12" y1="2" x2="12" y2="6" />
          <line x1="12" y1="18" x2="12" y2="22" />
          <line x1="2" y1="12" x2="6" y2="12" />
          <line x1="18" y1="12" x2="22" y2="12" />
        </svg>
        <svg v-else-if="option.icon === 'trending-up'" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
          <polyline points="16 7 22 7 22 13" />
        </svg>
        <span>{{ option.label }}</span>
      </button>
    </div>
  </div>
</template>
